import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { FileEdit, Trash2, Clock, RefreshCw } from "lucide-react";

const formatFull = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true });
};

const formatShort = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true });
};

const formatHM = (dec) => {
  if (dec == null) return "—";
  const m = Math.max(0, Math.round(dec * 60));
  return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, "0")}m`;
};

export default function TimeClockChangelogDialog({ open, onOpenChange }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/timeclock/changelog");
      setEvents(res.data || []);
    } catch {
      setEvents([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="changelog-dialog">
        <DialogHeader className="flex-row items-center justify-between gap-3">
          <div>
            <DialogTitle className="font-['Outfit'] text-xl tracking-tight">Time Entry Audit Log</DialogTitle>
            <p className="text-xs text-slate-500 mt-0.5 font-['Manrope']">
              Every edit and deletion to staff time entries. Owner-only.
            </p>
          </div>
          <button onClick={load} title="Refresh"
            className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6 pb-2 pt-1" data-testid="changelog-list">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-sm text-slate-400 font-['Manrope']">Loading…</div>
          ) : events.length === 0 ? (
            <div className="text-center py-16">
              <Clock className="w-10 h-10 text-slate-200 mx-auto mb-3" strokeWidth={1.5} />
              <p className="text-sm text-slate-500 font-['Manrope']">No audit events yet. When you edit or delete a time entry, it will appear here.</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100 border border-slate-100 rounded-xl bg-white">
              {events.map(e => (
                <div key={`${e.entry_id}-${e.event_at}`} className="p-4 flex items-start gap-3 hover:bg-slate-50/60 transition-colors" data-testid={`changelog-row-${e.entry_id}-${e.event_type}`}>
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    e.event_type === "delete" ? "bg-rose-50 text-rose-600" : "bg-amber-50 text-amber-600"
                  }`}>
                    {e.event_type === "delete" ? <Trash2 className="w-4 h-4" strokeWidth={2} /> : <FileEdit className="w-4 h-4" strokeWidth={2} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded font-['Manrope'] ${
                        e.event_type === "delete" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"
                      }`}>
                        {e.event_type === "delete" ? "Deleted" : "Edited"}
                      </span>
                      <p className="text-sm font-semibold text-slate-900 font-['Outfit']">{e.staff_name}</p>
                      <span className="text-slate-300 text-xs">·</span>
                      <p className="text-xs text-slate-500 font-['Manrope']">by {e.actor_name}</p>
                    </div>
                    <p className="text-[11px] text-slate-400 font-['Manrope'] mt-0.5">{formatFull(e.event_at)}</p>

                    <div className="mt-2 bg-slate-50 rounded-lg px-3 py-2 grid grid-cols-3 gap-2 text-[11px] tabular-nums font-['Manrope']">
                      <div>
                        <p className="text-[9px] uppercase tracking-wider font-bold text-slate-400">Clock In</p>
                        <p className="text-slate-700 font-medium">{formatShort(e.clock_in)}</p>
                      </div>
                      <div>
                        <p className="text-[9px] uppercase tracking-wider font-bold text-slate-400">Clock Out</p>
                        <p className="text-slate-700 font-medium">{e.clock_out ? formatShort(e.clock_out) : "—"}</p>
                      </div>
                      <div>
                        <p className="text-[9px] uppercase tracking-wider font-bold text-slate-400">Duration</p>
                        <p className="text-slate-900 font-semibold">{formatHM(e.total_hours)}</p>
                      </div>
                    </div>
                    {e.notes && <p className="text-xs text-slate-500 mt-2 italic font-['Manrope']">"{e.notes}"</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
